import { useState } from "react";
import HazardManagementService from "../services/hazard-management.service";
import type { AxiosResponse } from "axios";
import { useFeatureFlags } from "../contexts/FeatureFlags";
import type { UploadFile } from "../components/Upload";

export type UploadResult = UploadSuccess | UploadFailure | undefined;
export type UploadSuccess = { success: true, id: string };
export type UploadFailure = { success: false, error: string};

export function useSecureHoldingFileUpload(hazardService: (typeof HazardManagementService)) {
    const [files, setFiles] = useState<UploadFile[]>([]);
    const [isLoading, setIsLoading] = useState<Boolean>(false);

    const { ENABLE_SECURE_HOLDING_UPLOAD_HAZARDS } = useFeatureFlags();

    async function uploadAndRetriveHoldingId(): Promise<UploadResult> {
        if (!ENABLE_SECURE_HOLDING_UPLOAD_HAZARDS) {
            return;
        }

        if (files.length === 0) {
            return {success: false, error: "No files selected."};
        }

        try {
            setIsLoading(true);
            var response = await hazardService.uploadSecureHoldingFiles(files.map(f => f.file));
            return {success: true, id: response.data.holdingUploadId};
        } catch (e: any) {
            return handleUploadFailure(e.response);
        } finally {
            setIsLoading(false);
        }
    }

    function handleUploadFailure(errorResponse: AxiosResponse<any>): UploadFailure {
        // TODO - Consider other cases which don't populate apierror data
        let reason: string;
        if (errorResponse?.data?.apierror?.message !== undefined) {
            reason = errorResponse.data.apierror.message;
        } else {
            reason = "Encountered an error uploading files.";
        }

        // Update file state with errors
        const maybeSubErrors = errorResponse?.data?.apierror?.subErrors;
        if (maybeSubErrors !== undefined) {
            const fileNamesWhichHaveError: string[] = maybeSubErrors.map((f: { fileName: string }) => f.fileName);
            const newFiles = files.map(file => {
                if (fileNamesWhichHaveError.indexOf(file.file.name) !== -1) {
                    const updatedFile: UploadFile = { ...file, hasError: true };
                    return updatedFile;
                } else {
                    return file;
                }
            });

            setFiles(newFiles);
        }

        return { success: false, error: reason };
    }

    function removeFileByFileName(fileToRemoveName: String) {
        const fileToRemove = files.find(f => f.file.name === fileToRemoveName);
        if (fileToRemove === undefined) {
            console.warn(`Could not find file to remove: ${fileToRemoveName}`);
            return;
        }

        setFiles(existingFiles => existingFiles.filter(f => f !== fileToRemove));

        // Release object URL to allow file to be garbage collected.
        URL.revokeObjectURL(fileToRemove.previewUrl);
    }

    return { files, setFiles, removeFileByFileName, uploadAndRetriveHoldingId, isLoading };
}
